import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import type { ReuseAcquireCounts } from "@/features/registryChangeAlert/utils/buildReuseAcquireRequest";

interface IProps {
  open: boolean;
  counts: ReuseAcquireCounts;
  isReadOnly: boolean;
  disableCancel: boolean;
  onCancel: () => void;
  onConfirm: (cancelLoading: () => void) => void;
}

/** 変動アラートの新しい登記をまとめてリユース取得する前の確認モーダル。 */
export const ReuseAcquireConfirmDialog: React.FC<IProps> = ({
  open,
  counts,
  isReadOnly,
  disableCancel,
  onCancel,
  onConfirm,
}) => {
  const isMobile = useIsMobile();
  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onCancel();
      }}
      aria-labelledby="reuse-acquire-dialog-title"
    >
      <DialogTitle id="reuse-acquire-dialog-title">確認画面</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <Typography>
            【課金が発生します】新しい登記をリユースで取得します。
          </Typography>
          <Box component="ul" sx={{ pl: 5, my: 2 }}>
            {counts.shoyushajiko > 0 && (
              <Typography component="li">
                所有者事項：{counts.shoyushajiko.toLocaleString()}件
              </Typography>
            )}
            {counts.zenbujiko > 0 && (
              <Typography component="li">
                全部事項：{counts.zenbujiko.toLocaleString()}件
              </Typography>
            )}
          </Box>
          <Typography>
            ※リユース登記の料金が発生します。新規の取得は行いません。
          </Typography>
          <Typography>
            ※ 画面の切り替わりまでに時間がかかる場合があります
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          mb: 1,
          // SP は 2 つのボタンを縦に積む（横並びだと「取得する」が折り返す）
          flexDirection: { xs: "column-reverse", [MOBILE_BREAKPOINT]: "row" },
          gap: 1,
          "& > :not(style) ~ :not(style)": { ml: 0 },
          "& > *": { width: { xs: "100%", [MOBILE_BREAKPOINT]: "auto" } },
        }}
      >
        <Button
          variant={ButtonVariantOption.Outlined}
          onClick={onCancel}
          disabled={disableCancel}
          label={"キャンセル"}
        />
        <ReadOnlyTooltip isReadOnly={isReadOnly}>
          <SubmitButton
            timeout={120000}
            variant={ButtonVariantOption.Contained}
            onClick={(_event, cancelLoading) => {
              onConfirm(cancelLoading);
            }}
            label={"取得する"}
            disabled={isReadOnly}
          />
        </ReadOnlyTooltip>
      </DialogActions>
    </Dialog>
  );
};
